import { Link, useSearchParams } from 'react-router-dom'
import {
  PLATFORM_CAPABILITIES,
  PLATFORM_FLOW_STEPS,
  PLATFORM_HERO_CONTENT,
  PLATFORM_OPERATIONAL_BOUNDARIES,
  PLATFORM_PRINCIPLES,
  PLATFORM_PRODUCT_PROOF,
  PLATFORM_ROADMAP,
  PLATFORM_SECTION_INTROS,
  PLATFORM_STATUS_STRIP,
  type PlatformMode,
} from '../data/labMock'
import { usePageMetadata } from '../hooks/usePageMetadata'

const MODE_OPTIONS: Array<{ id: PlatformMode; label: string }> = [
  { id: 'public', label: 'Overview' },
  { id: 'technical', label: 'Technical detail' },
]

const MODE_CALLOUT = {
  public: 'Public-facing view: product behavior, current capabilities, and operating limits.',
  technical: 'Technical view: system sequence, stack layers, context release, and review separation.',
} satisfies Record<PlatformMode, string>

const TileIcon = ({ id }: { id: string }) => {
  const stroke = 'currentColor'

  if (id === 'source-aware' || id === 'context') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h12" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  }

  if (id === 'provenance') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 3l8 5v8l-8 5-8-5V8zM8.5 11.5h7M8.5 14.5h5"
          fill="none"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (id === 'separation' || id === 'review') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5 7h14M5 12h8M5 17h14M17 10l2 2-2 2"
          fill="none"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (id === 'release-control' || id === 'delivery') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5 12l4 4 10-10"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14zm0-2v4m0 12v4m10-10h-4M6 12H2"
        fill="none"
        stroke={stroke}
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

const StepIcon = ({ index }: { index: number }) => {
  const stroke = 'currentColor'

  if (index === 0) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 5h16v10H4zM7 18h10" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  }

  if (index === 1) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 12h14M12 5v14" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  }

  if (index === 2) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M6 7h12v10H6zM9 10h6M9 13h4"
          fill="none"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (index === 3) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5 19l6-6 4 4 4-8"
          fill="none"
          stroke={stroke}
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 12h10m0 0-3-3m3 3-3 3M20 6v12"
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

const StackIllustration = () => (
  <div className="platform-stack-figure" role="img" aria-label="Places in Time stack illustration">
    <div className="platform-stack-column">
      <p className="platform-stack-label">Interface</p>
      <div className="platform-stack-node platform-stack-node-highlight">Chat UI</div>
      <div className="platform-stack-node">Places and figures</div>
      <div className="platform-stack-node">Context and sources</div>
    </div>

    <div className="platform-stack-connector" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>

    <div className="platform-stack-column">
      <p className="platform-stack-label">Application</p>
      <div className="platform-stack-node platform-stack-node-highlight">Conversation orchestration</div>
      <div className="platform-stack-node">Persona and thread state</div>
      <div className="platform-stack-node">Response delivery</div>
    </div>

    <div className="platform-stack-connector" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>

    <div className="platform-stack-column">
      <p className="platform-stack-label">Evidence layer</p>
      <div className="platform-stack-node platform-stack-node-highlight">Active context release</div>
      <div className="platform-stack-node">Retrieval and works</div>
      <div className="platform-stack-node">Review surface</div>
    </div>
  </div>
)

export default function PlatformOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const mode: PlatformMode = searchParams.get('mode') === 'technical' ? 'technical' : 'public'
  const leadProof = PLATFORM_PRODUCT_PROOF.find((item) => item.layout === 'lead') ?? PLATFORM_PRODUCT_PROOF[0]
  const supportingProof = PLATFORM_PRODUCT_PROOF.filter((item) => item.layout === 'support')

  usePageMetadata({
    title: 'Platform Overview | Places in Time',
    description:
      'See how Places in Time combines curated historical sources, conversational AI, and public history design into a structured learning platform.',
    path: '/platform-overview',
  })

  const handleModeChange = (nextMode: PlatformMode) => {
    const nextParams = new URLSearchParams(searchParams)
    if (nextMode === 'public') {
      nextParams.delete('mode')
    } else {
      nextParams.set('mode', 'technical')
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <main className="page platform-overview-page">
      <div className="platform-overview-shell">
        <section className="platform-hero">
          <div className="platform-hero-top">
            <div className="platform-hero-titleblock">
              <p className="platform-hero-eyebrow">{PLATFORM_HERO_CONTENT.eyebrow[mode]}</p>
              <h1>Places in Time Platform Overview</h1>
            </div>

            <div className="platform-mode-toggle" role="group" aria-label="Overview content mode">
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="platform-mode-button"
                  aria-pressed={mode === option.id}
                  onClick={() => handleModeChange(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <p className="platform-hero-tagline">{PLATFORM_HERO_CONTENT.subline[mode]}</p>

          <div className="platform-hero-grid">
            <div className="platform-hero-main">
              <p className="platform-hero-subhead">{PLATFORM_HERO_CONTENT.intro[mode]}</p>

              <div className="platform-hero-summary">
                <h2 className="platform-hero-kicker">{PLATFORM_HERO_CONTENT.summaryLabel[mode]}</h2>
                <ul className="platform-hero-list">
                  {PLATFORM_HERO_CONTENT.summaryItems.map((item, index) => (
                    <li key={`hero-summary-${index}`}>{item[mode]}</li>
                  ))}
                </ul>
              </div>

              <div className="button-row platform-hero-actions">
                <Link className="button" to="/places">
                  Browse places
                </Link>
                <Link className="button primary" to="/chat">
                  Open chat
                </Link>
              </div>
            </div>

            <aside className="platform-glass-card platform-hero-callout" aria-label="Reference scope">
              <p className="platform-hero-callout-label">{PLATFORM_HERO_CONTENT.scopeLabel[mode]}</p>
              <p>{PLATFORM_HERO_CONTENT.scopeBody[mode]}</p>
            </aside>
          </div>

          <p className="platform-mode-summary">{MODE_CALLOUT[mode]}</p>
        </section>

        <section className="platform-stats-strip" aria-label="Current platform status">
          {PLATFORM_STATUS_STRIP.map((item) => (
            <article key={item.id} className="platform-stat-card">
              <p className="platform-stat-title">{item.title[mode]}</p>
              <p className="platform-stat-detail">{item.detail[mode]}</p>
            </article>
          ))}
        </section>

        <section className="platform-panel platform-panel-proof">
          <div className="platform-section-heading">
            <div className="platform-section-copy">
              <p className="platform-section-label">Product reference</p>
              <h2>{PLATFORM_SECTION_INTROS.proof.heading}</h2>
            </div>
            <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.proof.intro[mode]}</p>
          </div>

          <div className="platform-proof-showcase">
            <article className="platform-proof-module platform-proof-module-lead">
              <div className="platform-proof-media platform-proof-media-lead">
                {leadProof.kind === 'image' && leadProof.imageSrc ? (
                  <img src={leadProof.imageSrc} alt={leadProof.imageAlt} loading="lazy" />
                ) : (
                  <StackIllustration />
                )}
              </div>
              <div className="platform-proof-copy">
                <p className="platform-proof-label">{leadProof.label}</p>
                <h3>{leadProof.title[mode]}</h3>
                <p>{leadProof.description[mode]}</p>
              </div>
            </article>

            <div className="platform-proof-grid">
              {supportingProof.map((item) => (
                <article key={item.id} className="platform-proof-module platform-proof-module-support">
                  <div className="platform-proof-media">
                    {item.kind === 'image' && item.imageSrc ? (
                      <img src={item.imageSrc} alt={item.imageAlt} loading="lazy" />
                    ) : (
                      <StackIllustration />
                    )}
                  </div>
                  <div className="platform-proof-copy">
                    <p className="platform-proof-label">{item.label}</p>
                    <h3>{item.title[mode]}</h3>
                    <p>{item.description[mode]}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {mode === 'public' ? (
          <section className="platform-panel platform-panel-principles">
            <div className="platform-section-heading">
              <div className="platform-section-copy">
                <p className="platform-section-label">{PLATFORM_SECTION_INTROS.principles.heading}</p>
                <h2>What keeps it trustworthy</h2>
              </div>
              <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.principles.intro[mode]}</p>
            </div>

            <div className="platform-principles-list">
              {PLATFORM_PRINCIPLES.map((item) => (
                <article key={item.id} className="platform-principle-row">
                  <span className="platform-card-icon">
                    <TileIcon id={item.id} />
                  </span>
                  <div className="platform-principle-copy">
                    <h3>{item.title[mode]}</h3>
                    <p>{item.description[mode]}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : (
          <section className="platform-panel">
            <div className="platform-section-heading">
              <div className="platform-section-copy">
                <p className="platform-section-label">{PLATFORM_SECTION_INTROS.process.heading}</p>
                <h2>System sequence</h2>
              </div>
              <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.process.intro[mode]}</p>
            </div>

            <ol className="platform-flow-list">
              {PLATFORM_FLOW_STEPS.map((step, index) => (
                <li key={step.id} className="platform-flow-item">
                  <div className="platform-flow-head">
                    <span className="platform-flow-number">{`0${index + 1}`}</span>
                    <span className="platform-card-icon">
                      <StepIcon index={index} />
                    </span>
                  </div>
                  <div className="platform-flow-copy">
                    <h3>{step.title[mode]}</h3>
                    <p>{step.description[mode]}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className="platform-panel">
          <div className="platform-section-heading">
            <div className="platform-section-copy">
              <p className="platform-section-label">{PLATFORM_SECTION_INTROS.capabilities.heading}</p>
              <h2>What is currently live</h2>
            </div>
            <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.capabilities.intro[mode]}</p>
          </div>

          <div className="platform-capabilities-list">
            {PLATFORM_CAPABILITIES.map((item) => (
              <article key={item.id} className="platform-capability-row">
                <h3>{item.title[mode]}</h3>
                <p>{item.description[mode]}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="platform-panel">
          <div className="platform-section-heading">
            <div className="platform-section-copy">
              <p className="platform-section-label">{PLATFORM_SECTION_INTROS.boundaries.heading}</p>
              <h2>What the platform deliberately limits</h2>
            </div>
            <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.boundaries.intro[mode]}</p>
          </div>

          <div className="platform-boundaries-grid">
            {PLATFORM_OPERATIONAL_BOUNDARIES.map((item) => (
              <article key={item.id} className="platform-boundary-card">
                <h3>{item.title[mode]}</h3>
                <p>{item.description[mode]}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="platform-panel">
          <div className="platform-section-heading">
            <div className="platform-section-copy">
              <p className="platform-section-label">{PLATFORM_SECTION_INTROS.roadmap.heading}</p>
              <h2>What is coming next</h2>
            </div>
            <p className="platform-section-intro">{PLATFORM_SECTION_INTROS.roadmap.intro[mode]}</p>
          </div>

          <div className="platform-roadmap-grid">
            {PLATFORM_ROADMAP.map((column) => (
              <article key={column.id} className="platform-glass-card platform-roadmap-card">
                <h3>{column.title[mode]}</h3>
                <ul>
                  {column.items.map((item, index) => (
                    <li key={`${column.id}-${index}`}>{item[mode]}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  )
}
